import {
  parseFrontmatter,
  pathToSlugFromGlobKey,
  renderMarkdownToSafeHtml,
} from './blogMarkdown'

export interface BlogPostSummary {
  slug: string
  title: string
  date: string
  description: string
  tags: string[]
}

export interface BlogPost extends BlogPostSummary {
  html: string
}

const rawByPath = import.meta.glob('../content/blog/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>

function buildPosts(): BlogPost[] {
  const posts: BlogPost[] = []
  for (const [path, raw] of Object.entries(rawByPath)) {
    const slug = pathToSlugFromGlobKey(path)
    if (!slug) continue
    const { data, body } = parseFrontmatter(raw)
    const title = data.title?.trim() || slug
    const date = data.date?.trim() || '1970-01-01'
    const description = data.description?.trim() || ''
    // Tags are comma-separated in frontmatter: tags: spf, dkim, dmarc
    const tags = data.tags
      ? data.tags.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean)
      : []
    const html = renderMarkdownToSafeHtml(body)
    posts.push({ slug, title, date, description, tags, html })
  }
  posts.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
  return posts
}

let cached: BlogPost[] | null = null

export function getAllBlogPosts(): BlogPost[] {
  if (!cached) {
    cached = buildPosts()
  }
  return cached
}

export function getBlogPostBySlug(slug: string): BlogPost | undefined {
  return getAllBlogPosts().find((p) => p.slug === slug)
}

export function getBlogSummaries(): BlogPostSummary[] {
  return getAllBlogPosts().map(({ slug, title, date, description, tags }) => ({
    slug,
    title,
    date,
    description,
    tags,
  }))
}
